import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ArrowRight, Mail, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/api/blog`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/api/newsletter/subscribe`, {
        email: email.trim(),
        name: name.trim() || null
      });
      
      if (response.data.already_subscribed) {
        toast.info(response.data.message);
      } else {
        toast.success(response.data.message);
        setSubscribed(true);
      }
      setEmail('');
      setName('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-cyan-500 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-black uppercase mb-4">Blog</h1>
          <p className="text-xl font-light">Stories, updates & travel tips from Audiostride</p>
        </div>
      </header>

      {/* Newsletter Subscription */}
      <section className="bg-gradient-to-r from-blue-600 to-cyan-500 py-10 px-4">
        <div className="max-w-2xl mx-auto text-center">
          {subscribed ? (
            <div className="flex flex-col items-center gap-3 text-white">
              <CheckCircle className="w-12 h-12" />
              <h3 className="text-2xl font-bold">You're subscribed!</h3>
              <p className="text-white/80">Thanks for joining. We'll keep you updated with our latest posts.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center gap-2 mb-3">
                <Mail className="w-6 h-6 text-white" />
                <h3 className="text-xl font-bold text-white">Subscribe to Our Newsletter</h3>
              </div>
              <p className="text-white/80 mb-6">Get the latest travel tips, tour updates, and exclusive content delivered to your inbox.</p>
              
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="flex-1 px-4 py-3 rounded-full text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-white"
                  data-testid="newsletter-name-input"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 px-4 py-3 rounded-full text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-white"
                  data-testid="newsletter-email-input"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="bg-white text-cyan-600 px-6 py-3 rounded-full font-bold hover:bg-cyan-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="newsletter-subscribe-button"
                >
                  {subscribing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-cyan-600 border-t-transparent" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Subscribe
                    </>
                  )}
                </button>
              </form>
              <p className="text-white/60 text-xs mt-3">No spam, unsubscribe anytime.</p>
            </>
          )}
        </div>
      </section>

      {/* Blog Grid */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        {blogs.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-500 text-xl">No blog posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.map((blog) => (
              <Link
                key={blog.blog_id}
                to={`/blog/${blog.blog_id}`}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group"
                data-testid={`blog-card-${blog.blog_id}`}
              >
                {blog.cover_image_url ? (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={blog.cover_image_url}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                    <span className="text-6xl">📝</span>
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(blog.created_at)}</span>
                  </div>
                  
                  <h2 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-cyan-600 transition-colors">
                    {blog.title}
                  </h2>
                  
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {blog.excerpt}
                  </p>
                  
                  <div className="flex items-center text-cyan-600 font-semibold text-sm group-hover:gap-2 transition-all">
                    Read More <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default BlogPage;
